import { useEffect, useRef } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useTrigger } from '@atlassian/post-office-context';
import type { MigrateListConfigClickListener_JiraProject$key } from '@atlassian/jira-relay/src/__generated__/MigrateListConfigClickListener_JiraProject.graphql';
import type { MigrateListConfigClickListener_JiraView$key } from '@atlassian/jira-relay/src/__generated__/MigrateListConfigClickListener_JiraView.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { useListNINConfigMigration } from '../../../services/list-nin-config-migration/useListNINConfigMigration.tsx';

const MIGRATE_CONFIG_TRIGGER =
	'jira-nin-list-merge-modal-onboarding-explore-list-view-button-clicked-with-copy-list-settings';

type Props = {
	onChangeJqlAndGroupBy?: (params: { jql?: string; groupBy?: string }) => void;
	project: MigrateListConfigClickListener_JiraProject$key;
	view: MigrateListConfigClickListener_JiraView$key;
};

export const MigrateListConfigClickListener = ({ onChangeJqlAndGroupBy, project, view }: Props) => {
	const projectData = useFragment<MigrateListConfigClickListener_JiraProject$key>(
		graphql`
			fragment MigrateListConfigClickListener_JiraProject on JiraProject {
				projectId
				key
			}
		`,
		project,
	);

	const viewData = useFragment<MigrateListConfigClickListener_JiraView$key>(
		graphql`
			fragment MigrateListConfigClickListener_JiraView on JiraIssueSearchViewMetadata
			@argumentDefinitions(
				isSavedListViewTechnicalChangesDogfoodingEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-technical-changes-dogfooding-enabled.relayprovider"
				}
			) {
				id
				savedViewId @include(if: $isSavedListViewTechnicalChangesDogfoodingEnabled)
			}
		`,
		view,
	);

	let viewId = viewData.id;
	if (fg('saved_list_view_technical_changes_dogfooding')) {
		if (!viewData.savedViewId) {
			throw new Error('Missing saved view ID');
		}
		viewId = viewData.savedViewId;
	}

	const migrateListConfig = useListNINConfigMigration({
		projectId: projectData.projectId,
		projectKey: projectData.key,
		viewId,
		onChangeJqlAndGroupBy,
	});

	const hasClicked = useRef(false);

	const { isActive: isMigrateListConfigClicked } = useTrigger(MIGRATE_CONFIG_TRIGGER);

	useEffect(() => {
		if (!hasClicked.current && isMigrateListConfigClicked) {
			migrateListConfig();
			hasClicked.current = true;
		}
	}, [isMigrateListConfigClicked, migrateListConfig]);

	return null;
};
